"use client";

import { Fragment, useCallback, useRef, useState, type ReactNode } from "react";
import type { StickToBottomContext } from "use-stick-to-bottom";
import {
  ArrowRight,
  BookOpen,
  MessageCircle,
  Paperclip,
  SquarePen,
  Ticket,
  Upload,
  X,
} from "lucide-react";
import { ChatAttachment, ChatMessageProps } from "./chat-message";
import { AttachmentStrip, SentAttachments } from "./attachment-strip";
import { useFileAttachmentQueue } from "@/hooks/use-file-attachment-queue";
import { ChatInput } from "@/components/ui/chat-input";
import { Conversation, ConversationContent, ConversationScrollButton } from "@/components/ui/shadcn-io/ai/conversation";
import { Response } from "@/components/ui/shadcn-io/ai/response";
import { BlinkingDots } from "@/components/ui/shadcn-io/ai/loader";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

// Shown before the first exchange. Display-only — it is never sent to the backend,
// so it must describe what the Assistant can actually do.
const GREETING =
  "Ask me about anything in AIOS. I can answer, look things up in your account, or raise a ticket with the team.";

const ASSISTANT_UPLOAD_ENDPOINT = `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/aios-assistant/files`;
const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
const ACCEPTED_IMAGE_ATTR = "image/png,image/jpeg,image/webp";
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

const RAISE_TICKET_PROMPT = "Raise a ticket with the team";

const BIN_OPEN_MS = 260;
const CLEAR_SWEEP_MS = 900;
const CLEAR_STAGGER_MS = 78;
const BIN_SHUT_MS = 560;

const TRAILING_QUESTION = /\n\s*(Ready to send\?)\s*$/i;

const splitTrailingQuestion = (
  message: string
): { body: string; question: string | null } => {
  const match = message.match(TRAILING_QUESTION);
  if (!match) return { body: message, question: null };
  return {
    body: message.slice(0, match.index).trimEnd(),
    question: match[1],
  };
};

const BUBBLE = "max-w-[88%] px-[15px] py-3 text-sm leading-[1.55]";
const USER_BUBBLE = "self-end rounded-[16px_16px_4px_16px] bg-iai-purple text-white";
const ASSISTANT_BUBBLE =
  "self-start rounded-[16px_16px_16px_4px] bg-[#F4F3F9] text-iai-off-black dark:bg-iai-grey-light-2";

export interface ChatWindowProps {
  messages: ChatMessageProps[];
  onSendMessage: (message: string, attachments?: ChatAttachment[]) => void | Promise<void>;
  onClose: () => void;
  onClear?: () => void | Promise<void>;
  isLoading?: boolean;
  isHistoryLoading?: boolean;
  title?: string;
  className?: string;
  suggestedPrompts?: Array<{ label: string; prompt: string }>;
  prefill?: { token: number; text: string } | null;
}

function BinIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <svg
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn("size-[18px]", isOpen && "animate-bin-shake")}
      aria-hidden
    >
      <g
        className={cn(isOpen && "animate-bin-lid")}
        style={{ transformOrigin: "3.5px 5px" }}
      >
        <path d="M2.5 5h13" />
        <path d="M7 5V3.4a.9.9 0 0 1 .9-.9h2.2a.9.9 0 0 1 .9.9V5" />
      </g>
      <path d="M4.2 7.2l.65 7.3a1.2 1.2 0 0 0 1.2 1.1h5.9a1.2 1.2 0 0 0 1.2-1.1l.65-7.3" />
      {isOpen && (
        <path
          d="M6.6 9.4h4.8"
          className="animate-bin-stuff text-iai-purple"
          strokeWidth={2.2}
          style={{ transformOrigin: "center" }}
        />
      )}
    </svg>
  );
}

function QuickLink({
  icon: Icon,
  label,
  disabled = false,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex w-full items-center gap-3 bg-card px-3.5 py-2.5 text-left",
        "text-[13.5px] font-medium text-iai-navy transition-colors",
        "hover:bg-iai-purple-4 hover:text-iai-purple",
        "disabled:pointer-events-none disabled:opacity-50"
      )}
    >
      <Icon className="size-4 shrink-0 text-iai-mid-grey" />
      <span className="flex-1">{label}</span>
      <ArrowRight className="size-3.5 shrink-0 text-iai-mid-grey" />
    </button>
  );
}

// Shown while a stored conversation is being restored, so the greeting does not
// flash in and get replaced by history.
function ConversationSkeleton() {
  return (
    <div
      className="flex flex-col gap-3.5"
      aria-busy="true"
      aria-label="Loading your conversation"
    >
      {[
        "self-start w-[78%]",
        "self-end w-[52%]",
        "self-start w-[86%]",
        "self-end w-[40%]",
      ].map((placement, index) => (
        <Skeleton
          key={index}
          className={cn(
            "h-16 rounded-[16px]",
            index % 2 === 0 ? "rounded-bl-[4px]" : "rounded-br-[4px]",
            placement
          )}
        />
      ))}
    </div>
  );
}

export function ChatWindow({
  messages,
  onSendMessage,
  onClose,
  onClear,
  isLoading = false,
  isHistoryLoading = false,
  title = "Assistant",
  className,
  suggestedPrompts = [],
  prefill = null,
}: ChatWindowProps) {
  const conversationRef = useRef<StickToBottomContext | null>(null);
  const [showQuestions, setShowQuestions] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const clearButtonRef = useRef<HTMLButtonElement | null>(null);
  const bubbleRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const {
    attachedFiles,
    isDraggingOver,
    fileInputRef,
    handleFileSelect,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handlePaste,
    removeAttachedFile,
    clear: clearAttachments,
  } = useFileAttachmentQueue({
    endpoint: ASSISTANT_UPLOAD_ENDPOINT,
    maxFileSizeBytes: MAX_IMAGE_SIZE_BYTES,
    acceptedMimeTypes: ACCEPTED_IMAGE_TYPES,
  });

  const isUploading = attachedFiles.some((item) => item.status !== "done");

  // use-stick-to-bottom deliberately unsticks once the user scrolls up. Sending a
  // message means they are done reading history, so put them back at the newest reply.
  const handleSend = useCallback(
    (message: string) => {
      const attachments = attachedFiles.flatMap<ChatAttachment>((item) =>
        item.uploaded?.id
          ? [
              {
                id: item.uploaded.id,
                file_name: item.uploaded.file_name,
                file_url: item.uploaded.file_url ?? "",
                mime_type: item.uploaded.mime_type ?? item.file.type,
              },
            ]
          : []
      );
      const result = onSendMessage(message, attachments);
      clearAttachments();
      conversationRef.current?.scrollToBottom();
      return result;
    },
    [onSendMessage, attachedFiles, clearAttachments]
  );

  const handleClear = useCallback(async () => {
    if (!onClear) return;
    const target = clearButtonRef.current?.getBoundingClientRect();
    if (target) {
      const targetX = target.left + target.width / 2;
      const targetY = target.top + target.height / 2;
      bubbleRefs.current.forEach((node) => {
        const box = node.getBoundingClientRect();
        node.style.setProperty(
          "--clear-pull-x",
          `${targetX - (box.left + box.width / 2)}px`
        );
        node.style.setProperty(
          "--clear-pull-y",
          `${targetY - (box.top + box.height / 2)}px`
        );
      });
    }
    setIsClearing(true);
    clearAttachments();
    setShowQuestions(false);
    try {
      await new Promise((resolve) =>
        setTimeout(
          resolve,
          BIN_OPEN_MS +
            CLEAR_SWEEP_MS +
            Math.min(messages.length, 6) * CLEAR_STAGGER_MS
        )
      );
      await onClear();
      await new Promise((resolve) => setTimeout(resolve, BIN_SHUT_MS));
    } finally {
      setIsClearing(false);
    }
  }, [onClear, clearAttachments, messages.length]);

  const isWaitingForFirstReply =
    isLoading && (messages.length === 0 || messages[messages.length - 1]?.isUser);
  const isRestoringHistory = isHistoryLoading && messages.length === 0;
  const isFresh = messages.length === 0 && !isRestoringHistory;

  return (
    <div
      className={cn("relative flex h-full w-full flex-col bg-card", className)}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDraggingOver && (
        <div className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-1.5 rounded-[inherit] border-2 border-dashed border-iai-purple bg-iai-purple-4">
          <Upload className="size-6 text-iai-purple" aria-hidden />
          <span className="text-[13.5px] font-semibold text-iai-purple">
            Drop a screenshot to attach
          </span>
          <span className="text-[11.5px] text-iai-mid-grey">
            PNG, JPEG or WebP · max 10 MB
          </span>
        </div>
      )}

      {/* ph2: 16/20 pad, 15px/700 title, 28px purple icon tile */}
      <div className="flex items-center justify-between gap-3 border-b border-iai-border px-5 py-4">
        <h4 className="flex min-w-0 items-center gap-[9px] text-[15px] font-bold text-iai-off-black">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-iai-purple text-white">
            <MessageCircle className="size-[15px]" strokeWidth={2.2} />
          </span>
          <span className="truncate">{title}</span>
        </h4>

        <div className="flex shrink-0 items-center gap-0.5">
          {onClear && messages.length > 0 && (
            <>
            <button
              ref={clearButtonRef}
              type="button"
              onClick={handleClear}
              disabled={isLoading || isClearing}
              title="New chat"
              className="relative rounded-lg p-1.5 text-iai-mid-grey transition-colors hover:bg-iai-purple-4 hover:text-iai-navy disabled:opacity-50"
            >
              {isClearing ? (
                <BinIcon isOpen />
              ) : (
                <SquarePen className="size-[18px]" />
              )}
              <span className="sr-only">New chat</span>
            </button>
            </>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-iai-mid-grey transition-colors hover:bg-iai-purple-4 hover:text-iai-navy"
          >
            <X className="size-[18px]" />
            <span className="sr-only">Close the Assistant</span>
          </button>
        </div>
      </div>

      {/* log: 20px pad, 14px gap between bubbles */}
      <Conversation className="min-h-0 flex-1" contextRef={conversationRef}>
        <ConversationContent
          className="relative flex flex-col gap-3.5 p-5"
          style={isClearing ? { perspective: "1100px" } : undefined}
        >
          {isRestoringHistory ? (
            <ConversationSkeleton />
          ) : messages.length === 0 ? (
            <div className={cn(BUBBLE, ASSISTANT_BUBBLE)}>{GREETING}</div>
          ) : (
            messages.map((msg, index) => {
              const isLast = index === messages.length - 1;
              const isStreamingThisOne =
                isLoading && isLast && !msg.isUser && !msg.message?.trim();
              const { body, question } =
                msg.isUser || (isLoading && isLast)
                  ? { body: msg.message, question: null }
                  : splitTrailingQuestion(msg.message ?? "");

              const total = messages.length;
              const vacuumProps = isClearing
                ? ({
                    animationDelay: `${BIN_OPEN_MS + index * CLEAR_STAGGER_MS}ms`,
                    "--clear-spin": `${(msg.isUser ? 1 : -1) * (18 + (index % 3) * 7)}deg`,
                  } as React.CSSProperties)
                : undefined;

              const responseClasses = cn(
                // Response renders bold as span.font-semibold, not <strong>.
                "overflow-hidden break-words [&_p+p]:mt-2 [&_ol]:my-1.5 [&_ul]:my-1.5",
                msg.isUser
                  ? "[&_.font-semibold]:text-white [&_a]:text-white"
                  : "[&_.font-semibold]:text-iai-navy [&_a]:font-semibold [&_a]:text-iai-purple [&_a]:no-underline [&_a:hover]:underline"
              );

              return (
                <Fragment key={msg.id ?? `msg-${index}`}>
                  <div
                    ref={(node) => {
                      if (node) bubbleRefs.current.set(index * 2, node);
                      else bubbleRefs.current.delete(index * 2);
                    }}
                    className={cn(
                      BUBBLE,
                      msg.isUser ? USER_BUBBLE : ASSISTANT_BUBBLE,
                      isClearing && "animate-clear-vacuum"
                    )}
                    style={vacuumProps}
                  >
                    {body ? (
                      <Response className={responseClasses}>{body}</Response>
                    ) : null}
                    {msg.attachments?.length ? (
                      <SentAttachments attachments={msg.attachments} />
                    ) : null}
                    {isStreamingThisOne && <BlinkingDots size="md" />}
                  </div>
                  {question ? (
                    <div
                      ref={(node) => {
                        if (node) bubbleRefs.current.set(index * 2 + 1, node);
                        else bubbleRefs.current.delete(index * 2 + 1);
                      }}
                      className={cn(
                        BUBBLE,
                        ASSISTANT_BUBBLE,
                        isClearing && "animate-clear-vacuum"
                      )}
                      style={vacuumProps}
                    >
                      <Response className={responseClasses}>{question}</Response>
                    </div>
                  ) : null}
                </Fragment>
              );
            })
          )}

          {isWaitingForFirstReply && (
            <div className={cn(BUBBLE, ASSISTANT_BUBBLE)}>
              <BlinkingDots size="md" />
            </div>
          )}
        </ConversationContent>
        <ConversationScrollButton />
      </Conversation>

      {isFresh && showQuestions && suggestedPrompts.length > 0 && (
        <div className="shrink-0 px-4 pb-3 pt-0.5">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-iai-mid-grey">
              Try one of these
            </span>
            <button
              type="button"
              onClick={() => setShowQuestions(false)}
              className="text-[12px] font-medium text-iai-mid-grey transition-colors hover:text-iai-purple"
            >
              Back
            </button>
          </div>
          <div className="flex flex-wrap gap-[7px]">
            {suggestedPrompts.map((suggestion) => (
              <button
                key={suggestion.label}
                type="button"
                disabled={isLoading || isUploading}
                onClick={() => handleSend(suggestion.prompt)}
                className={cn(
                  "rounded-pill border border-iai-border bg-card px-3.5 py-[9px] text-left",
                  "text-[12.5px] font-medium text-iai-navy transition-colors min-[701px]:py-[7px]",
                  "hover:border-iai-purple hover:bg-iai-purple-4 hover:text-iai-purple",
                  "disabled:pointer-events-none disabled:opacity-50"
                )}
              >
                {suggestion.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {isFresh && !showQuestions && (
        <div className="shrink-0 px-4 pb-3 pt-0.5">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-iai-mid-grey">
            Or jump to
          </div>
          <div className="divide-y divide-iai-border overflow-hidden rounded-xl border border-iai-border">
            {suggestedPrompts.length > 0 && (
              <QuickLink
                icon={BookOpen}
                label="Help centre"
                onClick={() => setShowQuestions(true)}
              />
            )}
            <QuickLink
              icon={Ticket}
              label="Raise a ticket"
              disabled={isLoading || isUploading}
              onClick={() => handleSend(RAISE_TICKET_PROMPT)}
            />
          </div>
        </div>
      )}

      {/* pin: input stays enabled while loading; only send is disabled */}
      <div className="shrink-0 border-t border-iai-border px-4 py-3.5">
        <ChatInput
          onSend={handleSend}
          isLoading={isLoading || isUploading}
          placeholder="Ask me anything"
          prefill={prefill}
          onPaste={handlePaste}
          allowEmptyMessage={attachedFiles.length > 0}
          aboveSlot={
            <AttachmentStrip
              items={attachedFiles}
              onRemove={removeAttachedFile}
            />
          }
          leadingSlot={
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_ATTR}
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                aria-label="Attach a screenshot"
                className="flex size-[38px] shrink-0 items-center justify-center rounded-full border border-iai-border text-iai-dark-grey transition-colors hover:border-iai-purple hover:text-iai-purple"
              >
                <Paperclip className="size-4" strokeWidth={2.1} />
              </button>
            </>
          }
        />
      </div>
    </div>
  );
}
